import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const MAX_ENTRIES = 20;

type RecentEntryType = 'app' | 'workflow' | 'project';

export interface RecentEntry {
  type: RecentEntryType;
  displayName: string;
  location: string;
  project: string;
  app: string;
  /** Only set for workflow entries */
  workflow?: string;
  /** Full href path for navigation */
  href: string;
  lastVisitedAt: number;
  tenantId: string;
}

type RecentWorkState = {
  version: 1;
  entries: RecentEntry[];
};

export interface RemoveRecentScope {
  tenantId: string;
  /** Match entries in this project. */
  project?: string;
  /** Match entries for this app. When set with workflow undefined, also removes child workflow entries. */
  app?: string;
  /** Match a specific workflow. */
  workflow?: string;
}

type RecentWorkActions = {
  recordAccess: (entry: Omit<RecentEntry, 'lastVisitedAt'>) => void;
  getRecent: (tenantId: string, limit?: number) => RecentEntry[];
  /**
   * Remove entries matching the given scope. Useful for cleaning up after
   * delete operations so the Recent Activity list never shows dead links.
   *
   * Scope semantics (all conditions AND'd):
   * - `tenantId` (required) — only touch entries for this tenant
   * - `project`              — match only entries inside this project
   * - `app`                  — match this app and its child workflow entries
   * - `workflow`             — match only this specific workflow entry
   *
   * Passing only `tenantId` (no project/app/workflow) is a no-op. Use
   * `clearAll` if the intent is to wipe everything.
   */
  removeByScope: (scope: RemoveRecentScope) => void;
  clearAll: () => void;
};

type RecentWorkStore = RecentWorkState & RecentWorkActions;

export const useRecentWorkStore = create<RecentWorkStore>()(
  persist(
    (set, get) => ({
      version: 1,
      entries: [],

      recordAccess: (entry) =>
        set((state) => {
          const now = Date.now();
          const existing = state.entries.findIndex(
            (e) => e.href === entry.href && e.tenantId === entry.tenantId,
          );

          let next: RecentEntry[];
          if (existing !== -1) {
            // Update timestamp and displayName, move to front
            const updated: RecentEntry = {
              ...state.entries[existing]!,
              displayName: entry.displayName,
              tenantId: entry.tenantId,
              lastVisitedAt: now,
            };
            next = [updated, ...state.entries.filter((_, i) => i !== existing)];
          } else {
            // New entry — prepend
            const newEntry: RecentEntry = {
              type: entry.type,
              displayName: entry.displayName,
              location: entry.location,
              project: entry.project,
              app: entry.app,
              href: entry.href,
              tenantId: entry.tenantId,
              lastVisitedAt: now,
              ...(entry.workflow !== undefined ? { workflow: entry.workflow } : {}),
            };
            next = [newEntry, ...state.entries];
          }

          return { entries: next.slice(0, MAX_ENTRIES) };
        }),

      getRecent: (tenantId, limit = MAX_ENTRIES) =>
        get()
          .entries.filter((e) => e.tenantId === tenantId)
          .sort((a, b) => b.lastVisitedAt - a.lastVisitedAt)
          .slice(0, limit),

      removeByScope: (scope) =>
        set((state) => {
          // Guard against the empty-scope footgun: calling
          // `removeByScope({ tenantId })` with no other filter would silently
          // delete every recent entry for the tenant. Callers should use
          // `clearAll` for that, so a no-op here is the safer default.
          if (
            scope.project === undefined &&
            scope.app === undefined &&
            scope.workflow === undefined
          ) {
            return state;
          }
          return {
            entries: state.entries.filter((e) => {
              if (e.tenantId !== scope.tenantId) return true;
              if (scope.project !== undefined && e.project !== scope.project) return true;
              if (scope.app !== undefined && e.app !== scope.app) return true;
              if (scope.workflow !== undefined && e.workflow !== scope.workflow) return true;
              return false;
            }),
          };
        }),

      clearAll: () => set({ entries: [] }),
    }),
    {
      name: 'otto-recent-work',
      partialize: (state) => ({
        version: state.version,
        entries: state.entries,
      }),
      storage: {
        getItem: (name) => {
          try {
            const raw = localStorage.getItem(name);
            return raw ? JSON.parse(raw) as { state: RecentWorkState } : null;
          } catch {
            try { localStorage.removeItem(name); } catch { /* storage unavailable */ }
            return null;
          }
        },
        setItem: (name, value) => {
          try { localStorage.setItem(name, JSON.stringify(value)); } catch { /* quota exceeded or private mode */ }
        },
        removeItem: (name) => {
          try { localStorage.removeItem(name); } catch { /* noop */ }
        },
      },
    }
  )
);
